import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useProgress } from "../contexts/ProgressContext";
import Header from "../components/Header";
import LanguageToggle from "../components/LanguageToggle";
import { BookOpen, Layers, CheckSquare, Activity, FileText, BookMarked, Library, ChevronLeft, ChevronRight, RefreshCw, Clock } from "lucide-react";
import ChapterDiagrams from "../components/ChapterDiagrams";
import ChapterHero from "../components/ChapterHero";
import SourceView from "../components/SourceView";
import ChapterResources from "../components/ChapterResources";
import Mindmap from "../components/lesson/Mindmap";
import Infographic from "../components/lesson/Infographic";

const TABS = [
  { key: "lesson", icon: BookOpen },
  { key: "flashcards", icon: Layers },
  { key: "quiz", icon: CheckSquare },
  { key: "scenario", icon: Activity },
  { key: "summary", icon: FileText },
  { key: "source", icon: BookMarked },
  { key: "resources", icon: Library },
];

export default function Chapter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { t, isRTL, lang } = useLanguage();
  const { recordView, recordQuiz } = useProgress();

  const [chapter, setChapter] = useState(null);
  const initialTab = sp.get("tab");
  const [tab, setTab] = useState(TABS.some((x) => x.key === initialTab) ? initialTab : "lesson");
  const [cache, setCache] = useState({}); // {`${kind}_${lang}`: data}
  const [loadingKey, setLoadingKey] = useState(null);
  const [notReady, setNotReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    api.get(`/chapters/${id}`).then((r) => setChapter(r.data)).catch(() => setError("not found"));
  }, [id]);

  const load = useCallback(
    async (kind, lng) => {
      const key = `${kind}_${lng}`;
      if (cache[key]) return;
      setLoadingKey(key);
      setNotReady(false);
      setError("");
      try {
        const r = await api.get(`/chapters/${id}/content/${kind}`, { params: { lang: lng } });
        setCache((p) => ({ ...p, [key]: r.data }));
        // Record view (fire-and-forget)
        recordView(id, kind);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail === "CONTENT_NOT_READY" || e?.response?.status === 404) {
          setNotReady(true);
        } else {
          setError(detail || t("failedLoad"));
        }
      } finally {
        setLoadingKey(null);
      }
    },
    [id, cache, t]
  );

  useEffect(() => {
    if (!chapter) return;
    // Source and resources tabs read directly from their own endpoints — no cache fetch needed.
    if (tab === "source") {
      recordView(id, "source");
      return;
    }
    if (tab === "resources") {
      recordView(id, "resources");
      return;
    }
    load(tab, lang);
  }, [tab, lang, chapter, load, id, recordView]);

  const key = `${tab}_${lang}`;
  const content = cache[key];
  const busy = loadingKey === key;

  if (authLoading || !chapter) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-sage">{t("loading")}</div>;
  }

  return (
    <div className="min-h-screen bg-bone">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Link
          to="/dashboard"
          data-testid="back-to-chapters"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-sage transition-colors mb-6"
        >
          {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          {t("backToChapters")}
        </Link>

        <div className="mb-6 animate-fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-sage mb-3">
            {t("chapter")} 0{chapter.number}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            {chapter.title}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">{chapter.description}</p>
        </div>

        <ChapterHero chapterId={id} title={chapter.title} />

        <div className="flex flex-wrap gap-1 mb-8 p-1.5 bg-white border border-border rounded-2xl" data-testid="content-tabs">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.key;
            return (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                data-testid={`tab-${tb.key}`}
                className={`flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active ? "bg-sage text-white shadow-sm" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.8} />
                {t(tb.key)}
              </button>
            );
          })}
        </div>

        <div className="min-h-[400px]">
          {tab !== "source" && tab !== "resources" && busy && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-sage" data-testid="content-loading">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-sage animate-bounce" />
                <span className="w-2 h-2 rounded-full bg-sage animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-2 h-2 rounded-full bg-sage animate-bounce" style={{ animationDelay: "240ms" }} />
              </div>
              <div className="text-xs uppercase tracking-[0.2em]">{t("loading")}</div>
            </div>
          )}

          {tab !== "source" && tab !== "resources" && !busy && notReady && (
            <div className="bg-white border border-border rounded-2xl p-10 text-center" data-testid="content-not-ready">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-sage/10 flex items-center justify-center text-sage mb-4">
                <Clock className="w-6 h-6" strokeWidth={1.7} />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">
                {isRTL ? "هذا المحتوى قيد التحضير" : "This content is being prepared"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {isRTL
                  ? `${lang === "ar" ? "الترجمة العربية" : "المحتوى"} سيصبح متاحًا قريبًا. يقوم المسؤول بتوليد المحتوى الآن.`
                  : `${lang === "ar" ? "The Arabic translation" : "This content"} will appear here once the administrator generates it.`}
              </p>
            </div>
          )}

          {tab !== "source" && tab !== "resources" && !busy && error && !notReady && (
            <div className="text-terracotta text-sm flex items-center gap-3">
              {error}
              <button
                onClick={() => load(tab, lang)}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-white border border-border hover:bg-secondary"
                data-testid="retry-button"
              >
                <RefreshCw className="w-3.5 h-3.5" /> {t("retry")}
              </button>
            </div>
          )}

          {tab === "source" && <SourceView chapterId={id} />}

          {tab === "resources" && <ChapterResources chapterId={id} />}

          {tab !== "source" && tab !== "resources" && !busy && !notReady && !error && content && (
            <div className="animate-fade-up" key={key}>
              {tab === "lesson" && <LessonView data={content} chapterId={id} />}
              {tab === "flashcards" && <FlashcardsView data={content} />}
              {tab === "quiz" && <QuizView data={content} chapterId={id} onSubmit={recordQuiz} />}
              {tab === "scenario" && <ScenarioView data={content} />}
              {tab === "summary" && <SummaryView data={content} />}
            </div>
          )}
        </div>
      </main>
      <LanguageToggle />
    </div>
  );
}

function LessonView({ data, chapterId }) {
  const { t, lang } = useLanguage();
  const sections = data.sections || [];
  // Drop the infographic roughly halfway through the sections for visual pacing.
  const infoAfter = Math.max(0, Math.floor(sections.length / 2));
  const mindmapAfter = sections.length - 1;
  return (
    <div className="space-y-10">
      <article className="bg-white border border-border rounded-2xl p-6 sm:p-10" data-testid="lesson-content">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{data.title}</h2>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{data.intro}</p>

        <div className="mt-10 space-y-12">
          {sections.map((s, i) => (
            <React.Fragment key={`sec-${i}-${(s.heading || "").slice(0, 20)}`}>
              <SectionBlock section={s} index={i} />
              {data.infographic && i === infoAfter && (
                <Infographic data={data.infographic} />
              )}
              {data.mindmap && i === mindmapAfter && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-sage font-semibold">
                      Concept map
                    </span>
                  </div>
                  <Mindmap data={data.mindmap} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {data.clinical_pearls?.length > 0 && (
          <div className="mt-14 rounded-2xl bg-gradient-to-br from-sage/10 to-terracotta/5 border border-sage/30 p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-sage font-semibold mb-4 inline-flex items-center gap-2">
              <span className="w-6 h-px bg-sage" />
              {t("clinicalPearls")}
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {data.clinical_pearls.map((p, i) => (
                <li key={`pearl-${i}`} className="flex items-start gap-3 bg-white rounded-xl border border-border p-4 text-sm leading-relaxed">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-sage text-white text-[11px] font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
      <ChapterDiagrams chapterId={chapterId} lang={lang} />
    </div>
  );
}

const SECTION_ACCENTS = [
  { stripe: "border-sage", dot: "bg-sage", pill: "bg-sage/10 text-sage" },
  { stripe: "border-terracotta", dot: "bg-terracotta", pill: "bg-terracotta/10 text-terracotta" },
  { stripe: "border-amber-500", dot: "bg-amber-500", pill: "bg-amber-500/10 text-amber-700" },
  { stripe: "border-indigo-500", dot: "bg-indigo-500", pill: "bg-indigo-500/10 text-indigo-700" },
  { stripe: "border-teal-500", dot: "bg-teal-500", pill: "bg-teal-500/10 text-teal-700" },
  { stripe: "border-violet-500", dot: "bg-violet-500", pill: "bg-violet-500/10 text-violet-700" },
];

function SectionBlock({ section: s, index: i }) {
  const accent = SECTION_ACCENTS[i % SECTION_ACCENTS.length];
  return (
    <section data-testid={`lesson-section-${i}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold ${accent.pill}`}>
          {String(i + 1).padStart(2, "0")}
        </span>
        <h3 className={`font-display text-xl sm:text-2xl font-semibold tracking-tight border-s-4 ${accent.stripe} ps-4 py-1 flex-1`}>
          {s.heading}
        </h3>
      </div>

      <div className="space-y-4 text-base leading-relaxed text-foreground/90">
        {s.paragraphs?.map((p, j) => <p key={`p-${i}-${j}`}>{p}</p>)}
      </div>

      {s.callout && s.callout.body && <Callout data={s.callout} />}

      {s.key_points?.length > 0 && (
        <ul className="mt-5 grid sm:grid-cols-2 gap-2">
          {s.key_points.map((k, j) => (
            <li key={`kp-${i}-${j}`} className="flex items-start gap-3 bg-secondary/60 rounded-lg px-4 py-2.5 text-sm">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full ${accent.dot} shrink-0`} />
              <span>{k}</span>
            </li>
          ))}
        </ul>
      )}

      {s.table && Array.isArray(s.table.rows) && s.table.rows.length > 0 && (
        <LessonTable data={s.table} />
      )}
    </section>
  );
}

const CALLOUT_META = {
  definition: { bg: "bg-sage/5", border: "border-sage/30", pill: "bg-sage text-white", label: "Definition" },
  principle: { bg: "bg-indigo-500/5", border: "border-indigo-500/30", pill: "bg-indigo-600 text-white", label: "Principle" },
  warning: { bg: "bg-terracotta/5", border: "border-terracotta/40", pill: "bg-terracotta text-white", label: "Warning" },
  tip: { bg: "bg-amber-400/10", border: "border-amber-500/40", pill: "bg-amber-600 text-white", label: "Clinical tip" },
};

function Callout({ data }) {
  const meta = CALLOUT_META[data.kind] || CALLOUT_META.tip;
  return (
    <div className={`mt-5 rounded-2xl border ${meta.border} ${meta.bg} p-5 flex gap-4`} data-testid={`callout-${data.kind || "tip"}`}>
      <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.18em] font-bold ${meta.pill} shrink-0 self-start`}>
        {meta.label}
      </span>
      <div className="min-w-0">
        {data.title && <div className="font-semibold text-sm mb-1">{data.title}</div>}
        <div className="text-sm leading-relaxed">{data.body}</div>
      </div>
    </div>
  );
}

function LessonTable({ data }) {
  const cols = data.columns || [];
  const rows = data.rows || [];
  return (
    <figure className="mt-6 rounded-2xl border border-border bg-white overflow-hidden" data-testid="lesson-table">
      {data.title && (
        <figcaption className="px-5 py-3 bg-sage/5 text-sm font-medium border-b border-border">
          {data.title}
        </figcaption>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr>
              {cols.map((c, i) => (
                <th key={`th-${i}-${(c || "").slice(0, 12)}`} className="text-start font-semibold px-4 py-2.5 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={`tr-${r}`} className={r % 2 === 0 ? "bg-white" : "bg-bone/40"}>
                {(row || []).map((cell, c) => (
                  <td key={`td-${r}-${c}`} className="px-4 py-2.5 leading-relaxed border-t border-border align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function FlashcardsView({ data }) {
  const { t, isRTL } = useLanguage();
  const cards = data.cards || [];
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  if (!cards.length) return null;
  const card = cards[idx];
  const prev = () => { setFlipped(false); setIdx((i) => (i - 1 + cards.length) % cards.length); };
  const next = () => { setFlipped(false); setIdx((i) => (i + 1) % cards.length); };
  return (
    <div className="flex flex-col items-center" data-testid="flashcards-content">
      <div className="text-sm text-muted-foreground mb-4">{idx + 1} / {cards.length}</div>
      <div className="w-full max-w-2xl flashcard-3d">
        <div
          className={`flashcard-inner relative w-full h-[320px] cursor-pointer ${flipped ? "flipped" : ""}`}
          onClick={() => setFlipped((f) => !f)}
          data-testid="flashcard-flip-button"
        >
          <div className="flashcard-face front bg-white border border-sage/30 rounded-3xl p-8 sm:p-12 flex flex-col items-center justify-center text-center shadow-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-sage mb-4">Q</div>
            <div className="font-display text-2xl sm:text-3xl font-semibold leading-snug">{card.front}</div>
            <div className="mt-8 text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("flip")}</div>
          </div>
          <div className="flashcard-face back bg-sage text-white rounded-3xl p-8 sm:p-12 flex flex-col items-center justify-center text-center shadow-lg">
            <div className="text-xs uppercase tracking-[0.2em] text-white/80 mb-4">A</div>
            <div className="text-lg sm:text-xl leading-relaxed max-w-prose">{card.back}</div>
          </div>
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button onClick={prev} data-testid="flashcard-prev" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-border hover:bg-secondary transition-all text-sm">
          {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />} {t("previous")}
        </button>
        <button onClick={next} data-testid="flashcard-next" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sage hover:bg-sage-dark text-white transition-all text-sm">
          {t("next")} {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function QuizView({ data, chapterId, onSubmit }) {
  const { t } = useLanguage();
  const questions = data.questions || [];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = useMemo(() => {
    let s = 0;
    questions.forEach((q, i) => { if (answers[i] === q.correct_index) s += 1; });
    return s;
  }, [answers, questions]);
  const handleSubmit = () => {
    setSubmitted(true);
    if (onSubmit) onSubmit(chapterId, score, questions.length);
  };
  const reset = () => { setAnswers({}); setSubmitted(false); };
  return (
    <div className="space-y-6" data-testid="quiz-content">
      {questions.map((q, i) => {
        const selected = answers[i];
        return (
          <div key={`quiz-q-${i}-${(q.question || "").slice(0, 24)}`} className="bg-white border border-border rounded-2xl p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-sage mb-3">Q{i + 1}</div>
            <div className="font-display text-lg font-semibold leading-snug mb-5">{q.question}</div>
            <div className="space-y-2.5">
              {q.options.map((opt, j) => {
                const isSel = selected === j;
                const isCorrect = submitted && j === q.correct_index;
                const isWrong = submitted && isSel && j !== q.correct_index;
                return (
                  <button
                    key={`q${i}-opt-${j}-${(opt || "").slice(0, 16)}`}
                    onClick={() => !submitted && setAnswers((a) => ({ ...a, [i]: j }))}
                    disabled={submitted}
                    data-testid={`quiz-q${i}-opt${j}`}
                    className={`w-full text-start p-4 rounded-xl border transition-all flex items-center gap-3 ${
                      isCorrect ? "bg-sage/10 border-sage" : isWrong ? "bg-terracotta/10 border-terracotta" : isSel ? "bg-secondary border-sage" : "bg-white border-border hover:bg-secondary/50"
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold shrink-0 ${
                      isWrong ? "bg-terracotta text-white" : (isSel || isCorrect) ? "bg-sage text-white" : "bg-secondary text-muted-foreground"
                    }`}>
                      {String.fromCharCode(65 + j)}
                    </span>
                    <span className="text-sm leading-relaxed">{opt}</span>
                  </button>
                );
              })}
            </div>
            {submitted && q.explanation && (
              <div className="mt-4 text-sm text-muted-foreground bg-secondary/60 rounded-lg p-3 leading-relaxed">{q.explanation}</div>
            )}
          </div>
        );
      })}
      <div className="flex items-center justify-between bg-white border border-border rounded-2xl p-5">
        {!submitted ? (
          <>
            <div className="text-sm text-muted-foreground">{Object.keys(answers).length} / {questions.length}</div>
            <button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== questions.length}
              data-testid="quiz-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sage hover:bg-sage-dark disabled:opacity-40 text-white transition-all text-sm font-medium"
            >
              {t("submit")}
            </button>
          </>
        ) : (
          <>
            <div className="text-sm" data-testid="quiz-score">
              <span className="text-muted-foreground">{t("score")}: </span>
              <span className="font-bold text-sage text-lg">{score}</span>
              <span className="text-muted-foreground"> {t("of")} {questions.length}</span>
            </div>
            <button onClick={reset} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-border hover:bg-secondary text-sm font-medium">
              <RefreshCw className="w-4 h-4" /> {t("reset")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ScenarioView({ data }) {
  // Support both new ({scenarios: [...]}) and legacy (single scenario) formats.
  const scenarios = Array.isArray(data?.scenarios) && data.scenarios.length
    ? data.scenarios
    : [data];
  const [active, setActive] = useState(0);
  const scenario = scenarios[active] || scenarios[0];
  return (
    <div className="space-y-4" data-testid="scenario-content">
      {scenarios.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="scenario-tabs">
          {scenarios.map((s, i) => (
            <button
              key={`sc-${i}-${(s?.title || "").slice(0, 20)}`}
              onClick={() => setActive(i)}
              data-testid={`scenario-tab-${i}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${
                active === i
                  ? "bg-terracotta text-white"
                  : "bg-white border border-border text-muted-foreground hover:bg-secondary"
              }`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                active === i ? "bg-white/20" : "bg-terracotta/10 text-terracotta"
              }`}>{i + 1}</span>
              <span className="truncate max-w-[220px]">{s?.title || `Scenario ${i + 1}`}</span>
            </button>
          ))}
        </div>
      )}
      <SingleScenarioCard data={scenario} />
    </div>
  );
}

function SingleScenarioCard({ data }) {
  const { t } = useLanguage();
  const [revealed, setRevealed] = useState({});
  // Reset revealed answers when scenario changes
  useEffect(() => { setRevealed({}); }, [data]);
  if (!data) return null;
  return (
    <div className="bg-white border border-border rounded-2xl p-6 sm:p-10 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta mb-2">Scenario</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{data.title}</h2>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <InfoBlock label={t("setting")} value={data.setting} />
        <InfoBlock label={t("patient")} value={data.patient} />
        <InfoBlock label={t("situation")} value={data.situation} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sage font-semibold mb-3">{t("discussionQuestions")}</div>
        <div className="space-y-3">
          {data.questions?.map((q, i) => (
            <div key={`scq-${i}-${(q.prompt || "").slice(0, 20)}`} className="bg-secondary/50 rounded-xl p-5">
              <div className="font-medium leading-relaxed">{q.prompt}</div>
              <button
                onClick={() => setRevealed((r) => ({ ...r, [i]: !r[i] }))}
                className="mt-3 text-xs font-semibold text-sage hover:text-sage-dark uppercase tracking-[0.15em]"
                data-testid={`reveal-answer-${i}`}
              >
                {revealed[i] ? "Hide" : t("idealAnswer")}
              </button>
              {revealed[i] && (
                <div className="mt-3 text-sm leading-relaxed bg-white border border-border rounded-lg p-4">{q.ideal_answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
      {data.teaching_points?.length > 0 && (
        <div className="rounded-2xl bg-gradient-to-br from-sage/5 to-terracotta/5 border border-sage/20 p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-sage font-semibold mb-3">{t("teachingPoints")}</div>
          <ul className="space-y-2">
            {data.teaching_points.map((p, i) => (
              <li key={`tp-${i}-${(p || "").slice(0, 24)}`} className="flex items-start gap-3 text-sm leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="bg-secondary/50 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 font-semibold">{label}</div>
      <div className="text-sm leading-relaxed">{value}</div>
    </div>
  );
}

function SummaryView({ data }) {
  const { t } = useLanguage();
  return (
    <div className="bg-white border border-border rounded-2xl p-6 sm:p-10 space-y-8" data-testid="summary-content">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sage mb-3">Overview</div>
        <p className="text-lg leading-relaxed">{data.overview}</p>
      </div>
      <BulletBlock title={t("keyTakeaways")} items={data.key_takeaways} variant="sage" />
      <BulletBlock title={t("mustRemember")} items={data.must_remember} variant="terracotta" />
      <BulletBlock title={t("examTips")} items={data.exam_tips} variant="outline" />
    </div>
  );
}

function BulletBlock({ title, items, variant }) {
  if (!items?.length) return null;
  const styles = { sage: "bg-sage/5 border-sage/20", terracotta: "bg-terracotta/5 border-terracotta/20", outline: "bg-white border-border" }[variant];
  return (
    <div className={`rounded-xl border p-5 ${styles}`}>
      <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 text-foreground/80">{title}</div>
      <ul className="space-y-2">
        {items.map((p, i) => (
          <li key={`bi-${i}-${(p || "").slice(0, 20)}`} className="flex items-start gap-3 text-sm leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
